/**
 * The open month, live: the pieces the This month board and the project and
 * account "This month" cards share.
 *
 * Every figure is the engine's (`/api/profitability/month-to-date` and the
 * `month_to_date` key on project and account detail). This file decides how a
 * figure reads, never what it is. The rule that matters most: a margin the
 * engine could not price is null and reads "No cost rate" -- never a
 * percentage. A missing cost read as zero is a 100% margin, the most
 * flattering number a project can show and the one most certainly wrong.
 *
 * Aggregate callers get margin percent only; the hours and money keys are
 * ABSENT (not null), and the figures that need them are left out rather than
 * shown as dashes.
 */
import type { ReactNode } from "react";
import { MarginCell } from "@/components/profitability/MarginCell";
import { MarginFigure } from "@/components/profitability/MarginFigure";
import { HeroPanel } from "@/components/shared/hero-panel";
import { PanelTitle } from "@/components/shared/panel-title";
import { type ChipTone, StatusChip } from "@/components/shared/status-chip";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { calendarDay, hours as formatHours, money } from "@/lib/format";
import type {
  AccountMonthToDate,
  AggregateProjectMTD,
  ProjectMonthToDate,
  ProjectMTD,
} from "@/lib/profitability-api";

/** One project of either tier. The hours and money keys are absent for Aggregate. */
export type MtdRow = AggregateProjectMTD & Partial<Omit<ProjectMTD, keyof AggregateProjectMTD>>;

/** Whether the row's cost is fully, partly or not at all priced. */
export function costCoverage(row: MtdRow): "complete" | "partial" | "none" {
  if (row.cost_complete) return "complete";
  // Full tier: the engine nulls the landing margin when NO landing hour is
  // priced. Aggregate has no dollars, so a null projected margin on an
  // incomplete row is read the same way -- the cautious reading.
  if (row.landing_margin !== undefined) return row.landing_margin === null ? "none" : "partial";
  return row.landing_margin_pct === null ? "none" : "partial";
}

/** Margin so far is priced unless every hour to date is unpriced. */
export function pricedSoFar(row: MtdRow): boolean {
  if (row.margin !== undefined) return row.margin !== null;
  // Aggregate carries no dollars. A null margin so far on a row that is not
  // fully priced is read as unpriced: the projection may still be priced, so
  // the landing coverage cannot settle it, and "no revenue" would be the
  // wrong cause to print.
  return !(row.margin_pct === null && !row.cost_complete);
}

/** The projected margin is priced unless no landing hour is. */
export function pricedLanding(row: MtdRow): boolean {
  return costCoverage(row) !== "none";
}

/**
 * A margin the engine may not have been able to price.
 *
 * `priced` false means the margin itself is null for want of a cost rate:
 * "No cost rate", in words. A null percentage on a priced row is zero revenue,
 * which MarginCell already renders as a dash.
 */
export function MtdMargin({
  value,
  priced,
  headline = false,
}: {
  value: number | null;
  priced: boolean;
  /** At KPI size on a card, rather than table-cell size. */
  headline?: boolean;
}) {
  if (!priced) {
    return (
      <span
        className={`${headline ? "text-base font-medium" : "text-xs"} text-amber-700 dark:text-amber-400`}
        title="No cost rate behind these hours, so there is no margin to show."
      >
        No cost rate
      </span>
    );
  }
  return headline ? <MarginFigure value={value} /> : <MarginCell value={value} />;
}

/** How many flags a row carries, for sorting the Flags column. */
export function flagCount(row: MtdRow): number {
  return (
    (row.has_started ? 0 : 1) +
    (row.cost_complete ? 0 : 1) +
    ((row.unrated_billable_hours ?? 0) > 0 ? 1 : 0) +
    ((row.discount ?? 0) > 0 ? 1 : 0)
  );
}

const WARN = "warn" satisfies ChipTone;

/** Not started, cost coverage, no bill rate and pending discount, as pills. */
export function MtdFlags({ row }: { row: MtdRow }) {
  const coverage = costCoverage(row);
  const pills: { key: string; label: string; title?: string; tone: ChipTone }[] = [];
  if (!row.has_started) {
    pills.push({
      key: "not-started",
      label: "Not started",
      title: "Guidance this month, no hours logged yet.",
      tone: "neutral",
    });
  }
  if (coverage === "none") {
    pills.push({
      key: "no-cost",
      label:
        row.unpriced_hours === undefined
          ? "No cost rate"
          : `No cost rate: ${formatHours(row.unpriced_hours)} h`,
      title: "Nobody on these hours has a cost rate, so the project has no margin.",
      tone: WARN,
    });
  } else if (coverage === "partial") {
    pills.push({
      key: "partial",
      label: "Partial cost",
      title:
        row.unpriced_hours === undefined
          ? row.warnings.join(" ")
          : `${formatHours(row.unpriced_hours)} h have no cost rate (${formatHours(
              row.unpriced_hours_to_date ?? 0,
            )} h so far). Cost and margin cover only the priced hours.`,
      tone: WARN,
    });
  }
  if (row.unrated_billable_hours !== undefined && row.unrated_billable_hours > 0) {
    pills.push({
      key: "no-bill",
      label: `No bill rate: ${formatHours(row.unrated_billable_hours)} h`,
      title: "Billable hours on an assignment with no bill rate add no revenue.",
      tone: WARN,
    });
  }
  if (row.discount !== undefined && row.discount > 0) {
    pills.push({
      key: "discount",
      label: `Discount ${money(row.discount)}`,
      title: "A pending discount this month, already taken off the projected revenue.",
      tone: "neutral",
    });
  }
  if (pills.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {pills.map((pill) => (
        <StatusChip key={pill.key} tone={pill.tone} title={pill.title}>
          {pill.label}
        </StatusChip>
      ))}
    </div>
  );
}

// ── The card on project and account pages ──

function Shell({
  subtitle,
  help,
  children,
}: {
  subtitle?: string;
  help?: string;
  children: ReactNode;
}) {
  return (
    <Card data-testid="mtd-card" className="w-full border-0 bg-muted/50 shadow-none">
      <CardHeader>
        <PanelTitle help={help} className="flex-wrap items-baseline gap-x-2">
          This month
          {subtitle ? (
            <span className="font-body text-sm font-normal text-muted-foreground">{subtitle}</span>
          ) : null}
        </PanelTitle>
      </CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  );
}

function subtitleFor(period: { name: string }, asOf: string | null): string {
  return `${period.name} · ${asOf ? `Hours through ${calendarDay(asOf)}` : "No hours logged yet"}`;
}

/** The muted line for the two states that carry no figures. */
function Line({ children }: { children: ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

function Figure({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{children}</div>
    </div>
  );
}

const NOT_THIS_MONTH = "No hours or plan this month";
const BASIS_NOTE =
  "Live hours, not closed figures, and not in any closed-period total. Cost is each person's average over the last three closed months; the projection adds remaining guidance less pending discounts.";

/** A T&M project's open month. The engine sends nothing for other contract types. */
export function ProjectMonthToDateCard({
  slice,
  contractType,
}: {
  slice: ProjectMonthToDate;
  contractType?: string | null;
}) {
  if (slice.status === "unavailable") {
    return (
      <Shell>
        <Line>{slice.reason_text}</Line>
      </Shell>
    );
  }
  if (slice.status === "not_this_month") {
    return (
      <Shell subtitle={slice.period.name}>
        <Line>{NOT_THIS_MONTH}</Line>
      </Shell>
    );
  }
  const row: MtdRow = slice;
  const full = row.hours !== undefined;
  return (
    <div data-testid="mtd-card" className="w-full space-y-3">
      <HeroPanel
        eyebrow={`This month · ${subtitleFor(slice.period, slice.as_of)}`}
        figure={<MtdMargin value={row.landing_margin_pct} priced={pricedLanding(row)} headline />}
        status={contractType ? <StatusChip tone="info">{contractType}</StatusChip> : undefined}
        note={BASIS_NOTE}
        facts={[
          ...(full ? [{ label: "Hours so far", value: formatHours(row.hours ?? 0) }] : []),
          {
            label: "Margin so far",
            value: <MtdMargin value={row.margin_pct} priced={pricedSoFar(row)} />,
          },
          ...(full
            ? [
                { label: "Projected hours", value: formatHours(row.landing_hours ?? 0) },
                { label: "Projected revenue", value: money(row.landing_revenue ?? 0) },
              ]
            : []),
        ]}
      />
      <MtdFlags row={row} />
      {row.warnings.length > 0 ? (
        <ul className="space-y-0.5 text-xs text-muted-foreground">
          {row.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** An account's T&M projects this month, summed by the engine. */
export function AccountMonthToDateCard({ slice }: { slice: AccountMonthToDate }) {
  if (slice.status === "unavailable") {
    return (
      <Shell>
        <Line>{slice.reason_text}</Line>
      </Shell>
    );
  }
  if (slice.status === "not_this_month") {
    return (
      <Shell subtitle={slice.period.name}>
        <Line>{NOT_THIS_MONTH}</Line>
      </Shell>
    );
  }
  const full = slice.hours !== undefined;
  const excluded = slice.excluded_unpriced;
  // The engine's sums cover fully priced projects only. With none priced the
  // sums are zeros that describe nothing: dashes, not $0 and not a margin.
  const nothingPriced = excluded > 0 && excluded >= slice.project_count;
  const dash = <span className="text-muted-foreground">—</span>;
  const projects = `${slice.project_count} time-and-materials project${
    slice.project_count === 1 ? "" : "s"
  } with hours or guidance this month.`;
  return (
    <Shell subtitle={subtitleFor(slice.period, slice.as_of)} help={BASIS_NOTE}>
      <p className="text-sm text-muted-foreground">{projects}</p>
      <div className={`grid grid-cols-2 gap-4 ${full ? "sm:grid-cols-3 lg:grid-cols-5" : ""}`}>
        {full ? (
          <Figure label="Hours so far">
            {nothingPriced ? dash : formatHours(slice.hours ?? 0)}
          </Figure>
        ) : null}
        <Figure label="Margin so far">
          {nothingPriced ? dash : <MarginFigure value={slice.margin_pct} />}
        </Figure>
        {full ? (
          <Figure label="Projected hours">
            {nothingPriced ? dash : formatHours(slice.landing_hours ?? 0)}
          </Figure>
        ) : null}
        {full ? (
          <Figure label="Projected revenue">
            {nothingPriced ? dash : money(slice.landing_revenue ?? 0)}
          </Figure>
        ) : null}
        <Figure label="Projected margin">
          {nothingPriced ? dash : <MarginFigure value={slice.landing_margin_pct} />}
        </Figure>
      </div>
      {nothingPriced ? (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          No priced projects: every project here has hours with no cost rate.
        </p>
      ) : excluded > 0 ? (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          {excluded} of {slice.project_count} projects {excluded === 1 ? "has" : "have"} hours with
          no cost rate and {excluded === 1 ? "is" : "are"} left out of these figures.
        </p>
      ) : null}
      {(slice.discount ?? 0) > 0 && !nothingPriced ? (
        <p className="text-xs text-muted-foreground">
          Projected revenue is after {money(slice.discount ?? 0)} of pending discounts.
        </p>
      ) : null}
    </Shell>
  );
}
